import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Search, CalendarCheck, Truck, KeyRound, TrendingUp, Wallet, Building2, Store, Users, MessageSquare, CalendarDays, Sparkles, X, ChevronRight } from "lucide-react";

export const TOUR_KEY = "velocity_tour";
export const TOUR_EVENT = "velocity-tour";

const TOURS = {
  driver: [
    { icon: Search, t: "Find your next ride", d: "Search by city and dates. Filters for gig-ready cars, EVs and unlimited-mile plans.", to: "/browse", cta: "Browse cars" },
    { icon: CalendarCheck, t: "Book in minutes", d: "Pick dates, pay securely, chat with the host in-app. Deposits are held in escrow.", to: "/browse" },
    { icon: Truck, t: "Earn on the Gig Hub", d: "Delivery jobs, auto-transport loads and streak bonuses — all from your dashboard.", to: "/gig-hub", cta: "Open Gig Hub" },
    { icon: Sparkles, t: "Your dashboard", d: "Trips, payouts, Driver Card and alerts live here. You can replay this tour any time.", to: "/dashboard", cta: "Go to dashboard" },
  ],
  host: [
    { icon: KeyRound, t: "List your car", d: "Photos, VIN check and instant AI pricing. Listings go live after a quick review.", to: "/list-your-car", cta: "List a car" },
    { icon: TrendingUp, t: "Dynamic pricing", d: "Opt in to nightly AI re-pricing so your car stays booked at the best rate.", to: "/list-your-car" },
    { icon: Wallet, t: "Get paid", d: "Payouts release automatically after each trip. Track earnings and statements here.", to: "/dashboard" },
    { icon: Sparkles, t: "Your dashboard", d: "Bookings, maintenance reminders, disputes and payouts in one place.", to: "/dashboard", cta: "Go to dashboard" },
  ],
  business: [
    { icon: Building2, t: "Apply as a partner", d: "Dealers, shops and fleets: apply once to unlock the partner portal and referral codes.", to: "/partners/apply", cta: "Apply now" },
    { icon: Store, t: "Dealer portal", d: "Push inventory to Buy, sell underperformers and take offers against break-even.", to: "/dealers" },
    { icon: Truck, t: "Nationwide transport", d: "Post loads or get an instant transport quote for vehicles moving between lots.", to: "/transport", cta: "Transport board" },
    { icon: Sparkles, t: "Your dashboard", d: "Listings, sales, leads and payouts — all tracked here.", to: "/dashboard", cta: "Go to dashboard" },
  ],
  community: [
    { icon: Users, t: "The feed", d: "Posts from drivers and hosts near you. Follow members and share wins.", to: "/community", cta: "Open feed" },
    { icon: MessageSquare, t: "Groups & messages", d: "Join city groups, DM members and swap tips on cars, gigs and pricing.", to: "/community/groups" },
    { icon: CalendarDays, t: "Events", d: "Meetups, webinars and Academy sessions — RSVP in one tap.", to: "/community/events" },
    { icon: Sparkles, t: "Your dashboard", d: "Saved searches, watchlist and your Academy progress live here.", to: "/dashboard", cta: "Go to dashboard" },
  ],
};

export default function WelcomeTour() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  useEffect(() => {
    const check = () => { const forced = localStorage.getItem(TOUR_KEY) === "show"; if (user?.account_type && (forced || !user.tour_done)) { setI(0); setOpen(true); } };
    check();
    window.addEventListener(TOUR_EVENT, check);
    return () => window.removeEventListener(TOUR_EVENT, check);
  }, [user]);
  if (!open || !user?.account_type) return null;
  const steps = TOURS[user.account_type] || TOURS.driver;
  const s = steps[i];
  const last = i === steps.length - 1;
  const close = async (to) => {
    setOpen(false); localStorage.removeItem(TOUR_KEY);
    try { await api.post("/auth/tour-done"); } catch { /* ignore */ }
    refresh?.(); if (to) nav(to);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="welcome-tour">
      <div className="w-full max-w-md rounded-2xl border border-blue-500/40 bg-[#0a1120] p-6 shadow-[0_0_60px_-15px_rgba(59,130,246,.6)] rise">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[11px] tracking-widest uppercase text-blue-400">Welcome tour · {i + 1}/{steps.length}</p>
          <button onClick={() => close()} className="text-slate-500 hover:text-slate-200" aria-label="Skip tour" data-testid="tour-skip"><X className="w-4 h-4" /></button>
        </div>
        <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center mb-4"><s.icon className="w-6 h-6 text-blue-300" /></div>
        <h3 className="font-display text-2xl font-bold" data-testid="tour-title">{s.t}</h3>
        <p className="text-sm text-slate-400 mt-2">{s.d}</p>
        <div className="flex gap-1.5 mt-5">{steps.map((_, k) => <span key={k} className={`h-1 flex-1 rounded-full ${k <= i ? "bg-blue-400" : "bg-slate-800"}`} />)}</div>
        <div className="flex items-center gap-2 mt-5">
          {s.cta && <Button variant="outline" onClick={() => close(s.to)} className="h-10 border-slate-700 text-sm" data-testid="tour-cta">{s.cta}</Button>}
          <Button onClick={() => (last ? close(s.to) : setI(i + 1))} className="h-10 ml-auto bg-blue-500 hover:bg-blue-400 text-white font-semibold" data-testid="tour-next">{last ? "Finish" : "Next"}<ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </div>
    </div>
  );
}

export const replayTour = () => { localStorage.setItem(TOUR_KEY, "show"); window.dispatchEvent(new Event(TOUR_EVENT)); };
